import React, { useEffect, useMemo, useState } from "react";
import Modal from "../userProflePopup/UserProfileModal";
import MediaLibraryModal from "../mediaLibrary/MediaLibraryModal";
import InputText from "../inputText/InputText";
import Button from "../button/Button";
import style from "./CreatorEditProfile.module.css";
import {
  creatorGetProfile,
  creatorProfileUpdate,
  creatorChangePassword,
  deleteUserProfile,
} from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";
import {
  FaUser,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaImage,
  FaRegEdit,
  FaUserCircle,
  FaKey,
  FaTrashAlt,
} from "react-icons/fa";
import { MdOutlineCameraAlt } from "react-icons/md";
import { RxCross2 } from "react-icons/rx";

const DETAILS = "details";
const PASSWORD = "password";

/**
 * Polished, tabbed "Edit Profile" dialog for creators. Mirrors the signup
 * fields (name, phone, channel name/description, profile image, channel
 * banner) plus a password tab. Email is identity → shown read-only.
 *
 * - Details tab saves name/phone/channel + images in ONE call (creatorProfileUpdate),
 *   so the user isn't hunting across separate dialogs for image vs text.
 * - Password tab uses creatorChangePassword.
 *
 * `onUpdated` lets the parent refresh the profile widget after a save.
 */
const CreatorEditProfile = ({ isOpen, onClose, onUpdated }) => {
  const [activeTab, setActiveTab] = useState(DETAILS);

  // Form state
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [email, setEmail] = useState("");
  // A stored URL can point at a file that no longer exists. Without this the
  // <img> renders and fails, showing a broken box where the placeholder should
  // be - so a failed load falls back to the same placeholder as "no image".
  const [imgFailed, setImgFailed] = useState({ profile: false, banner: false });

  const [form, setForm] = useState({
    name: "",
    phone: "",
    channel_name: "",
    channel_description: "",
    profile_img: "", // URL shown in the avatar; sent as-is to the backend
    channel_banner: "", // URL shown in the banner tile
  });
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsErrors, setDetailsErrors] = useState({});

  // Password state
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [showPwd, setShowPwd] = useState({ current: false, next: false, confirm: false });
  const [savingPwd, setSavingPwd] = useState(false);
  const [pwdErrors, setPwdErrors] = useState({});

  // Media library picker: which target the picked image fills.
  const [mediaTarget, setMediaTarget] = useState(null); // "profile" | "banner" | null
  const [removingPhoto, setRemovingPhoto] = useState(false);

  // Fetch current profile to pre-fill when the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoadingProfile(true);
    (async () => {
      try {
        const res = await creatorGetProfile();
        const d = res?.data?.data || {};
        const ch = d.channel || {};
        if (cancelled) return;
        setEmail(d.email || "");
        setForm({
          name: d.name || "",
          phone: d.phone || "",
          channel_name: ch.channel_name || "",
          channel_description: ch.channel_description || "",
          profile_img: d.profile_img || "",
          channel_banner: ch.channel_banner || "",
        });
      } catch (error) {
        showToast(
          error?.response?.data?.message || "Couldn't load your profile",
          "error"
        );
      } finally {
        if (!cancelled) setLoadingProfile(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Reset transient state whenever the modal is freshly opened.
  useEffect(() => {
    if (isOpen) {
      setActiveTab(DETAILS);
      setPwd({ current: "", next: "", confirm: "" });
      setShowPwd({ current: false, next: false, confirm: false });
      setDetailsErrors({});
      setPwdErrors({});
    }
  }, [isOpen]);

  const handleField = (key) => (e) => {
    const value =
      key === "phone" ? e.target.value.replace(/\D/g, "").slice(0, 14) : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
    setDetailsErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handlePwdField = (key) => (e) => {
    setPwd((prev) => ({ ...prev, [key]: e.target.value }));
    setPwdErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handlePickedImage = (item) => {
    const url = item?.bunny_cdn_url;
    if (!url) {
      setMediaTarget(null);
      return;
    }
    if (mediaTarget === "profile") {
      setForm((prev) => ({ ...prev, profile_img: url }));
    } else if (mediaTarget === "banner") {
      setForm((prev) => ({ ...prev, channel_banner: url }));
    }
    setMediaTarget(null);
  };

  // Remove the profile photo — same capability the old image dialog had.
  // Hits DELETE /delete-profile-img (works for any logged-in user), then clears
  // the local preview and notifies the parent to refresh.
  const handleRemovePhoto = async () => {
    if (!form.profile_img || removingPhoto) return;
    setRemovingPhoto(true);
    try {
      const res = await deleteUserProfile();
      setForm((prev) => ({ ...prev, profile_img: "" }));
      showToast(res?.data?.message || "Profile photo removed", "success");
      onUpdated?.();
    } catch (error) {
      if (error?.response?.status === 401) {
        showToast("Session expired. Please log in again.", "error");
        onClose?.();
      } else {
        showToast(
          error?.response?.data?.message || "Couldn't remove photo",
          "error"
        );
      }
    } finally {
      setRemovingPhoto(false);
    }
  };

  const validateDetails = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (form.phone && (form.phone.length < 9 || form.phone.length > 14)) {
      errs.phone = "Phone must be 9–14 digits";
    }
    if (!form.channel_name.trim()) errs.channel_name = "Channel name is required";
    setDetailsErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSaveDetails = async () => {
    if (!validateDetails()) return;
    setSavingDetails(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone,
        channel_name: form.channel_name.trim(),
        channel_description: form.channel_description,
      };
      // Only send images when actually set, so we never blank an existing one.
      if (form.profile_img) payload.profile_img = form.profile_img;
      if (form.channel_banner) payload.channel_banner = form.channel_banner;

      const res = await creatorProfileUpdate(payload);
      showToast(res?.data?.message || "Profile updated", "success");
      onUpdated?.();
      onClose?.();
    } catch (error) {
      if (error?.response?.status === 401) {
        showToast("Session expired. Please log in again.", "error");
        onClose?.();
      } else {
        const data = error?.response?.data;
        const msg = data?.errors
          ? Object.values(data.errors).flat()[0]
          : data?.message;
        showToast(msg || "Update failed", "error");
      }
    } finally {
      setSavingDetails(false);
    }
  };

  const validatePwd = () => {
    const errs = {};
    if (!pwd.current) errs.current = "Enter your current password";
    if (!pwd.next) errs.next = "Enter a new password";
    else if (pwd.next.length < 6) errs.next = "Must be at least 6 characters";
    else if (pwd.next === pwd.current)
      errs.next = "New password must be different from the current one";
    if (pwd.confirm !== pwd.next) errs.confirm = "Passwords don't match";
    setPwdErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSavePassword = async () => {
    if (!validatePwd()) return;
    setSavingPwd(true);
    try {
      const res = await creatorChangePassword({
        current_password: pwd.current,
        new_password: pwd.next,
      });
      showToast(res?.data?.message || "Password changed", "success");
      setPwd({ current: "", next: "", confirm: "" });
      onClose?.();
    } catch (error) {
      if (error?.response?.status === 401) {
        // 401 here means the current password was wrong (token is still valid).
        setPwdErrors((prev) => ({
          ...prev,
          current: error?.response?.data?.message || "Current password is incorrect",
        }));
      } else {
        const data = error?.response?.data;
        showToast(data?.message || "Couldn't change password", "error");
      }
    } finally {
      setSavingPwd(false);
    }
  };

  const indicatorStyle = useMemo(
    () => ({ transform: activeTab === DETAILS ? "translateX(0)" : "translateX(100%)" }),
    [activeTab]
  );

  if (!isOpen) return null;

  return (
    <>
      {mediaTarget && (
        <MediaLibraryModal
          isOpen={!!mediaTarget}
          setIsOpen={(open) => {
            if (!open) setMediaTarget(null);
          }}
          mode="modal"
          handleimagesvalue={handlePickedImage}
          showCheckbox={true}
          type="image"
        />
      )}

      <Modal isOpen={isOpen} onClose={onClose} contentClassName={style.panel} hideClose>
        <div className={style.body}>
          {/* Header */}
          <div className={style.header}>
            <div className={style.headerText}>
              <h2 className={style.title}>Edit Profile</h2>
              <p className={style.subtitle}>
                Update your creator details, channel info, and password.
              </p>
            </div>
            <button className={style.closeBtn} onClick={onClose} aria-label="Close">
              <RxCross2 />
            </button>
          </div>

          {/* Tabs */}
          <div className={style.tabs} role="tablist">
            <span className={style.tabIndicator} style={indicatorStyle} />
            <button
              role="tab"
              aria-selected={activeTab === DETAILS}
              className={`${style.tab} ${activeTab === DETAILS ? style.tabActive : ""}`}
              onClick={() => setActiveTab(DETAILS)}
            >
              <FaUserCircle className={style.tabIcon} /> Details
            </button>
            <button
              role="tab"
              aria-selected={activeTab === PASSWORD}
              className={`${style.tab} ${activeTab === PASSWORD ? style.tabActive : ""}`}
              onClick={() => setActiveTab(PASSWORD)}
            >
              <FaKey className={style.tabIcon} /> Password
            </button>
          </div>

          {activeTab === DETAILS ? (
            <>
              <div className={style.form}>
                <div className={style.grid}>
                  {/* Media row: avatar + banner */}
                  <div className={style.mediaRow}>
                    <div className={style.avatarBlock}>
                      <div
                        className={style.avatar}
                        onClick={() => setMediaTarget("profile")}
                        title="Change profile picture"
                      >
                        {form.profile_img && !imgFailed.profile ? (
                          <img
                            src={form.profile_img}
                            alt="Profile"
                            className={style.avatarImg}
                            onError={() => setImgFailed((p) => ({ ...p, profile: true }))}
                          />
                        ) : (
                          <span className={style.avatarPlaceholder}>
                            <FaUser />
                          </span>
                        )}
                        <span className={style.avatarOverlay}>
                          <MdOutlineCameraAlt />
                        </span>
                      </div>
                      <div className={style.mediaMeta}>
                        <span className={style.mediaTitle}>Profile picture</span>
                        <div className={style.mediaActions}>
                          <button
                            type="button"
                            className={style.changeLink}
                            onClick={() => setMediaTarget("profile")}
                          >
                            Change photo
                          </button>
                          {form.profile_img && (
                            <button
                              type="button"
                              className={style.removeLink}
                              onClick={handleRemovePhoto}
                              disabled={removingPhoto}
                            >
                              <FaTrashAlt />
                              {removingPhoto ? "Removing…" : "Remove"}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div
                      className={style.bannerTile}
                      onClick={() => setMediaTarget("banner")}
                      title="Change channel banner"
                    >
                      {form.channel_banner && !imgFailed.banner ? (
                        <img
                          src={form.channel_banner}
                          alt="Channel banner"
                          className={style.bannerImg}
                          onError={() => setImgFailed((p) => ({ ...p, banner: true }))}
                        />
                      ) : (
                        <span className={style.bannerPlaceholder}>
                          <FaImage />
                          Channel banner
                        </span>
                      )}
                      <span className={style.bannerOverlay}>
                        <FaRegEdit /> Change banner
                      </span>
                    </div>
                  </div>

                  {/* Full Name */}
                  <div className={style.field}>
                    <label className={style.label}>Full Name</label>
                    <InputText
                      className={style.editField}
                      type="text"
                      value={form.name}
                      onChange={handleField("name")}
                      placeholder="Your full name"
                    />
                    {detailsErrors.name && (
                      <span className={style.errorText}>{detailsErrors.name}</span>
                    )}
                  </div>

                  {/* Phone */}
                  <div className={style.field}>
                    <label className={style.label}>Phone Number</label>
                    <InputText
                      className={style.editField}
                      type="text"
                      inputMode="numeric"
                      value={form.phone}
                      onChange={handleField("phone")}
                      placeholder="e.g. 9123456780"
                    />
                    {detailsErrors.phone && (
                      <span className={style.errorText}>{detailsErrors.phone}</span>
                    )}
                  </div>

                  {/* Email (read-only) */}
                  <div className={`${style.field} ${style.full}`}>
                    <label className={style.label}>
                      Email Address <span className={style.optional}>(can't be changed)</span>
                    </label>
                    <div className={style.lockedField}>
                      <span className={style.lockedValue}>{email || "—"}</span>
                      <FaLock className={style.lockIcon} />
                    </div>
                  </div>

                  {/* Channel Name */}
                  <div className={`${style.field} ${style.full}`}>
                    <label className={style.label}>Channel Name</label>
                    <InputText
                      className={style.editField}
                      type="text"
                      value={form.channel_name}
                      onChange={handleField("channel_name")}
                      placeholder="Your channel name"
                    />
                    {detailsErrors.channel_name && (
                      <span className={style.errorText}>{detailsErrors.channel_name}</span>
                    )}
                  </div>

                  {/* Channel Description */}
                  <div className={`${style.field} ${style.full}`}>
                    <label className={style.label}>
                      Channel Description <span className={style.optional}>(optional)</span>
                    </label>
                    <textarea
                      className={style.textarea}
                      value={form.channel_description}
                      onChange={handleField("channel_description")}
                      placeholder="Tell viewers what your channel is about…"
                    />
                  </div>
                </div>
              </div>

              <hr className={style.divider} />
              <div className={style.footer}>
                <Button
                  variant="outline"
                  className={style.cancelBtn}
                  onClick={onClose}
                  disabled={savingDetails}
                >
                  Cancel
                </Button>
                <Button
                  variant="dark"
                  className={style.saveBtn}
                  onClick={handleSaveDetails}
                  loading={savingDetails}
                  disabled={savingDetails || loadingProfile}
                >
                  Save Changes
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className={style.form}>
                <div className={style.grid}>
                  <div className={`${style.field} ${style.full}`}>
                    <label className={style.label}>Current Password</label>
                    <div className={style.passwordWrap}>
                      <InputText
                        className={style.editField}
                        type={showPwd.current ? "text" : "password"}
                        value={pwd.current}
                        onChange={handlePwdField("current")}
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        className={style.eyeBtn}
                        onClick={() =>
                          setShowPwd((p) => ({ ...p, current: !p.current }))
                        }
                        aria-label="Toggle password visibility"
                      >
                        {showPwd.current ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                    {pwdErrors.current && (
                      <span className={style.errorText}>{pwdErrors.current}</span>
                    )}
                  </div>

                  <div className={`${style.field} ${style.full}`}>
                    <label className={style.label}>New Password</label>
                    <div className={style.passwordWrap}>
                      <InputText
                        className={style.editField}
                        type={showPwd.next ? "text" : "password"}
                        value={pwd.next}
                        onChange={handlePwdField("next")}
                        placeholder="At least 6 characters"
                      />
                      <button
                        type="button"
                        className={style.eyeBtn}
                        onClick={() => setShowPwd((p) => ({ ...p, next: !p.next }))}
                        aria-label="Toggle password visibility"
                      >
                        {showPwd.next ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                    {pwdErrors.next ? (
                      <span className={style.errorText}>{pwdErrors.next}</span>
                    ) : (
                      <span className={style.hintText}>
                        Use 6+ characters, different from your current password.
                      </span>
                    )}
                  </div>

                  <div className={`${style.field} ${style.full}`}>
                    <label className={style.label}>Confirm New Password</label>
                    <div className={style.passwordWrap}>
                      <InputText
                        className={style.editField}
                        type={showPwd.confirm ? "text" : "password"}
                        value={pwd.confirm}
                        onChange={handlePwdField("confirm")}
                        placeholder="Re-enter new password"
                      />
                      <button
                        type="button"
                        className={style.eyeBtn}
                        onClick={() =>
                          setShowPwd((p) => ({ ...p, confirm: !p.confirm }))
                        }
                        aria-label="Toggle password visibility"
                      >
                        {showPwd.confirm ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                    {pwdErrors.confirm && (
                      <span className={style.errorText}>{pwdErrors.confirm}</span>
                    )}
                  </div>
                </div>
              </div>

              <hr className={style.divider} />
              <div className={style.footer}>
                <Button
                  variant="outline"
                  className={style.cancelBtn}
                  onClick={onClose}
                  disabled={savingPwd}
                >
                  Cancel
                </Button>
                <Button
                  variant="dark"
                  className={style.saveBtn}
                  onClick={handleSavePassword}
                  loading={savingPwd}
                  disabled={savingPwd}
                >
                  Update Password
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default CreatorEditProfile;
